export const CAPABILITY_MODULES = {
  ANALYTICS: 'analytics',
  BOOKINGS: 'bookings',
  STAFF: 'staff',
  VENUES: 'venues',
  REPORTING: 'reporting',
  INTEGRATIONS: 'integrations',
  BILLING: 'billing',
  PC_CAFE: 'pc-cafe',
  DASHBOARD: 'dashboard',
  CUSTOMERS: 'customers',
  PROMOTIONS: 'promotions',
  FOOD: 'food',
} as const;

export type ModuleKey = (typeof CAPABILITY_MODULES)[keyof typeof CAPABILITY_MODULES];

export const CAPABILITY_KEYS = {
  ANALYTICS_DASHBOARD: 'analytics.dashboard',
  ANALYTICS_TRENDS: 'analytics.trends',
  ANALYTICS_FORECASTING: 'analytics.forecasting',
  ANALYTICS_EXPORT: 'analytics.export',
  ANALYTICS_INSIGHTS: 'analytics.insights',
  ANALYTICS_GUEST_CONVERSION: 'analytics.guest_conversion',
  ANALYTICS_HEATMAP: 'analytics.heatmap',
  ANALYTICS_BOOKING_SOURCE: 'analytics.booking_source',
  ANALYTICS_CUSTOMER_ANALYTICS: 'analytics.customer_analytics',
  ANALYTICS_ADDON_ANALYTICS: 'analytics.addon_analytics',
  ANALYTICS_FOOD_ANALYTICS: 'analytics.food_analytics',
  ANALYTICS_EQUIPMENT_UTILIZATION: 'analytics.equipment_utilization',
  ANALYTICS_VENUE_PERFORMANCE: 'analytics.venue_performance',
  BOOKINGS_MANAGEMENT: 'bookings.management',
  BOOKINGS_AUTOMATION: 'bookings.automation',
  BOOKINGS_BULK_ACTIONS: 'bookings.bulk_actions',
  BOOKINGS_ACTIVE_SESSIONS: 'bookings.active_sessions',
  BOOKINGS_TABLE_MANAGEMENT: 'bookings.table_management',
  BOOKINGS_GROUP_MANAGEMENT: 'bookings.group_management',
  BOOKINGS_SHIFT_SUMMARY: 'bookings.shift_summary',
  STAFF_BASIC_ROLES: 'staff.basic_roles',
  STAFF_ADVANCED_PERMISSIONS: 'staff.advanced_permissions',
  STAFF_SCHEDULING: 'staff.scheduling',
  STAFF_ACTIVITY_LOG: 'staff.activity_log',
  VENUES_MANAGEMENT: 'venues.management',
  VENUES_MULTI_LOCATION: 'venues.multi_location',
  VENUES_WHITE_LABEL: 'venues.white_label',
  VENUES_PEAK_PRICING: 'venues.peak_pricing',
  VENUES_BUNDLES: 'venues.bundles',
  VENUES_LOW_STOCK_ALERTS: 'venues.low_stock_alerts',
  VENUES_FLOOR_PLAN: 'venues.floor_plan',
  VENUES_ADDONS: 'venues.addons',
  REPORTING_BASIC: 'reporting.basic',
  REPORTING_EXCEL_EXPORT: 'reporting.excel_export',
  REPORTING_SCHEDULED: 'reporting.scheduled',
  REPORTING_TAX_EXPORT: 'reporting.tax_export',
  INTEGRATIONS_WEBHOOKS: 'integrations.webhooks',
  INTEGRATIONS_API: 'integrations.api',
  BILLING_BASIC_INVOICES: 'billing.basic_invoices',
  BILLING_TAX_INVOICES: 'billing.tax_invoices',
  BILLING_CUSTOM_INVOICING: 'billing.custom_invoicing',
  BILLING_EARNINGS_FORECAST: 'billing.earnings_forecast',
  BILLING_PAYOUT_PDF: 'billing.payout_pdf',
  BILLING_TDS_CERTIFICATES: 'billing.tds_certificates',
  BILLING_TAX_REPORTS: 'billing.tax_reports',
  PC_CAFE_SESSION_MANAGEMENT: 'pc-cafe.session-management',
  PC_CAFE_LEADERBOARD: 'pc-cafe.leaderboard',
  PC_CAFE_ANALYTICS: 'pc-cafe.analytics',
  PC_CAFE_SESSION_NOTIFICATIONS: 'pc-cafe.session-notifications',
  PC_CAFE_AGENT_CONTROL: 'pc-cafe.agent-control',
  DASHBOARD_REVENUE_TARGET: 'dashboard.revenue_target',
  DASHBOARD_DAILY_SALES: 'dashboard.daily_sales',
  CUSTOMERS_SEGMENTS: 'customers.segments',
  CUSTOMERS_NOTES: 'customers.notes',
  CUSTOMERS_CREDIT_TRACKING: 'customers.credit_tracking',
  CUSTOMERS_PUSH_NOTIFICATIONS: 'customers.push_notifications',
  PROMOTIONS_COUPONS: 'promotions.coupons',
  PROMOTIONS_ADVANCED_RULES: 'promotions.advanced_rules',
  FOOD_DISCOUNTS: 'food.discounts',
  FOOD_OFFERS: 'food.offers',
} as const;

export type CapabilityKey = (typeof CAPABILITY_KEYS)[keyof typeof CAPABILITY_KEYS];

// Old FeatureKey -> new CapabilityKey[] mapping for migration
export const FEATURE_TO_CAPABILITY_MAP: Record<string, readonly CapabilityKey[]> = {
  analytics_advanced: [
    CAPABILITY_KEYS.ANALYTICS_TRENDS,
    CAPABILITY_KEYS.ANALYTICS_FORECASTING,
    CAPABILITY_KEYS.ANALYTICS_EXPORT,
  ],
  booking_automation: [CAPABILITY_KEYS.BOOKINGS_AUTOMATION],
  multi_location: [CAPABILITY_KEYS.VENUES_MULTI_LOCATION],
  custom_branding: [CAPABILITY_KEYS.VENUES_WHITE_LABEL],
  priority_support: [], // Dropped — SLA, not software feature
  api_access: [CAPABILITY_KEYS.INTEGRATIONS_API],
  staff_management: [CAPABILITY_KEYS.STAFF_BASIC_ROLES, CAPABILITY_KEYS.STAFF_ADVANCED_PERMISSIONS],
  reporting_export: [CAPABILITY_KEYS.REPORTING_EXCEL_EXPORT],
  tax_invoices: [CAPABILITY_KEYS.BILLING_TAX_INVOICES],
};

// Reverse mapping: CapabilityKey -> FeatureKey (for backward compat in guard)
// Used when hierarchical-entitlements flag is OFF: maps new capability keys back to old feature keys
export const CAPABILITY_TO_FEATURE_MAP: Record<string, string> = {};
for (const [featureKey, capKeys] of Object.entries(FEATURE_TO_CAPABILITY_MAP)) {
  for (const capKey of capKeys) {
    CAPABILITY_TO_FEATURE_MAP[capKey] = featureKey;
  }
}
